import React, { useEffect } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import ExclamationTriangleIcon from 'Assets/icons/exclamation-triangle.svg';

/**
 * A single project row in the picker list (IB-641): checkbox + name + budget
 * version dropdown (name · date). Surfaces an archived badge and a warning when
 * the previously-chosen budget version no longer exists (falls back to evolving).
 */
export default function PickerRow({ event, entry, onToggle, onChangeBudget }) {
    const enabled = !!entry?.enabled;
    const budgets = event.budgets || [];
    const selectedBudgetId = entry?.budget ?? '';
    const versionMissing = selectedBudgetId !== '' && !budgets.some((b) => b.id === selectedBudgetId);

    // When the saved budget version is no longer present, coerce the draft entry
    // to null (evolving) so onApply does not commit the stale id and downstream
    // services do not 500 on a non-null-but-missing budget id.
    useEffect(() => {
        if (versionMissing) {
            onChangeBudget(event.id, null);
        }
    }, [versionMissing, event.id, onChangeBudget]);

    return (
        <div className="tw-flex tw-items-center tw-gap-3 tw-py-2 tw-px-1">
            <label className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0 tw-cursor-pointer">
                <input type="checkbox" checked={enabled} onChange={() => onToggle(event.id)} className="tw-rounded tw-border-charcoal-25 tw-text-blue" />
                <span className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                    <span className={classNames('tw-text-sm tw-truncate', enabled ? 'tw-text-charcoal' : 'tw-text-charcoal-50')}>{event.name}</span>
                    {event.archived && (
                        <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-2xs tw-font-medium tw-bg-charcoal-10 tw-text-charcoal-75">
                            {$t('Archived')}
                        </span>
                    )}
                </span>
            </label>

            <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
                {versionMissing && (
                    <span title={$t('The saved budget version is no longer available; using the evolving budget.')}>
                        <ExclamationTriangleIcon className="tw-w-4 tw-h-4 tw-text-yellow-400" />
                    </span>
                )}
                <select
                    value={versionMissing ? '' : selectedBudgetId}
                    onChange={(e) => onChangeBudget(event.id, e.target.value ? Number(e.target.value) : null)}
                    disabled={!enabled}
                    className="tw-border-2 tw-border-grey tw-rounded-lg tw-text-xs tw-py-1 tw-ps-2 tw-pe-6 tw-bg-white tw-text-charcoal disabled:tw-opacity-50 tw-max-w-[180px]"
                >
                    {budgets.map((budget) => (
                        <option key={budget.id} value={budget.id}>
                            {budget.name}
                            {budget.created_at ? ` · ${moment(budget.created_at).format('DD MMM YYYY')}` : ''}
                            {budget.is_locked ? ` (${$t('locked')})` : ''}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}
